import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getExpectedCNAME } from '@gitbook/app-database';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Actions,
    alertKindPropsMap,
    Button,
    confirm,
    Description,
    DialogRoot,
    Group,
    Heading,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    getCustomHostnameStatusInfos,
    getPaidIndicatorBadgeProps,
    PaidFeatureIndicator,
    SiteCustomHostnameDialog,
} from '../../../../../Components';
import { apiClient, apiQuery, useSiteContext, useUpgradeSite } from '../../../../../state';

export const SetupCustomDomain = React.memo(function SetupCustomDomain() {
    const customHostnameDialogTrigger = useDialogTriggerState();
    const { site } = useSiteContext();

    return (
        <>
            {site.hostname ? (
                <ConfiguredCustomDomain
                    hostname={site.hostname}
                    onSetupDomain={customHostnameDialogTrigger.open}
                />
            ) : (
                <SetupSiteCustomDomain onSetupDomain={customHostnameDialogTrigger.open} />
            )}

            <DialogRoot {...customHostnameDialogTrigger.rootProps}>
                <SiteCustomHostnameDialog />
            </DialogRoot>
        </>
    );
});

function ConfiguredCustomDomain(props: { hostname: string; onSetupDomain: () => void }) {
    const { organizationId, site } = useSiteContext();
    const { data: customHostname } = useSuspenseQuery(
        apiQuery('getCustomHostname', { hostname: props.hostname }, { nullOnError: true })
    );

    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomDomain);
    const cname = getExpectedCNAME({
        type: 'organization',
        organization: organizationId,
    });
    const infos = getCustomHostnameStatusInfos(customHostname, cname);
    const alertProps = alertKindPropsMap[infos.kind ?? 'default'];
    const isActive = customHostname?.status === api.CustomHostnameSslStatus.Live;

    return (
        <ActionableContainer orientation="horizontal">
            <Group>
                <Group>
                    <alertProps.icon
                        className={clsx('m-nudge-icon size-4', alertProps.iconColor)}
                    />
                    <Heading>
                        {isActive ? 'Custom domain active' : `Custom domain ${props.hostname}`}
                    </Heading>
                    <PaidFeatureIndicator upgrade={upgrade} isFeatureActive={!!site.hostname} />
                </Group>
                <Description>
                    {isActive ? (
                        <>
                            Your custom domain is active on <strong>{props.hostname}</strong>.
                        </>
                    ) : (
                        infos.details
                    )}
                </Description>
            </Group>
            <Actions>
                {infos.action ? (
                    <Button kind="secondary" disabled={!!upgrade} {...infos.action} />
                ) : null}
                <CustomDomainPaletteOptionsButton
                    onConfigure={props.onSetupDomain}
                    hostname={props.hostname}
                />
            </Actions>
        </ActionableContainer>
    );
}

function SetupSiteCustomDomain(props: { onSetupDomain: () => void }) {
    const { site } = useSiteContext();
    const hasCustomProxy = Boolean(site.proxy);
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomDomain);

    return (
        <ActionableContainer orientation="horizontal">
            <Group>
                <Group>
                    <Heading>Domain</Heading>
                    <PaidFeatureIndicator upgrade={upgrade} />
                </Group>
                <Description>
                    Use your own domain to match your brand (e.g. docs.yourcompany.com).
                </Description>
            </Group>

            <Button
                disabled={hasCustomProxy}
                title={
                    hasCustomProxy
                        ? 'This site is already using a custom subdirectory. Please remove it before setting up a custom domain.'
                        : undefined
                }
                kind="secondary"
                size="medium"
                label="Set up a custom domain"
                leadingIcon={Icon.Globe}
                onPress={() => {
                    if (upgrade) {
                        return upgrade.onPress();
                    }
                    props.onSetupDomain();
                }}
                className="shrink-0"
            />
        </ActionableContainer>
    );
}

function CustomDomainPaletteOptionsButton(props: { onConfigure: () => void; hostname: string }) {
    const { hostname, onConfigure } = props;
    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomDomain);

    const removeDomain = async () => {
        void confirm({
            title: 'Remove domain?',
            description: `Content will no longer be served under the domain ${hostname}. You can configure it again later.`,
            kind: 'danger',
            confirmLabel: 'Remove',
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        await apiClient.customHostnames.removeCustomHostname(hostname);
                        toast.success('Domain removed');
                    },
                    'Error while removing domain',
                    { onReject: preventClose }
                );
            },
        });
    };

    return (
        <PaletteButton
            kind="secondary"
            label="Manage domain"
            leadingIcon={Icon.Globe}
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'commands',
                            commands: [
                                {
                                    key: 'config',
                                    title: 'Configure DNS',
                                    icon: Icon.Settings,
                                    trigger: upgrade ? upgrade.onPress : onConfigure,
                                    badge: getPaidIndicatorBadgeProps({
                                        upgrade,
                                        isFeatureActive: !!hostname,
                                    }),
                                },
                                {
                                    key: 'remove',
                                    title: 'Remove domain',
                                    icon: Icon.X,
                                    trigger: removeDomain,
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}
